import React, { useEffect, useRef } from 'react';

// Warp-speed starfield, adapted from react-starfield
// (https://github.com/designly1/react-starfield). Rewritten so the canvas
// fills its parent element and scrolls with the page instead of being pinned
// to the viewport, draws on a transparent background so no blend mode is
// needed, is device-pixel-ratio aware, and cancels its animation on unmount.
//
// The parent element must be positioned (relative/absolute) so the canvas can
// stretch over it.
const Starfield = ({
  starCount = 1500,
  starColor = [255, 255, 255],
  speedFactor = 0.03,
  className,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !parent || !ctx) return undefined;

    const [r, g, b] = starColor;
    let width = 0;
    let height = 0;
    let stars = [];
    let frame = 0;
    let last = null;

    const makeStars = () => {
      // Spread the field a little wider than the canvas so the edges of a
      // tall page still get stars drifting through.
      const xRange = Math.max(800, width * 0.7);
      const yRange = Math.max(450, height * 0.7);
      stars = Array.from({ length: starCount }, () => ({
        x: Math.random() * xRange * 2 - xRange,
        y: Math.random() * yRange * 2 - yRange,
        z: Math.random() * 1000,
      }));
    };

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      width = parent.clientWidth;
      height = parent.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      makeStars();
    };

    const draw = (delta) => {
      ctx.clearRect(0, 0, width, height);
      const cx = width / 2;
      // Vanishing point sits in the middle of the first screen (the hero),
      // so stars stream outward from where the eye lands on load.
      const cy = Math.min(height, window.innerHeight) / 2;
      for (let i = 0; i < stars.length; i++) {
        const star = stars[i];
        star.z -= delta * speedFactor;
        while (star.z <= 1) star.z += 1000;
        const x = cx + star.x / (star.z * 0.001);
        const y = cy + star.y / (star.z * 0.001);
        if (x < 0 || x >= width || y < 0 || y >= height) continue;
        const d = star.z / 1000;
        ctx.fillStyle = `rgba(${r},${g},${b},${1 - d * d})`;
        ctx.fillRect(x, y, 1, 1);
      }
    };

    const tick = (now) => {
      if (last === null) last = now;
      draw(now - last);
      last = now;
      frame = requestAnimationFrame(tick);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(parent);

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      draw(0);
    } else {
      frame = requestAnimationFrame(tick);
    }

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [starCount, starColor, speedFactor]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
};

export default Starfield;
